import { AuthConfigUnavailableError } from "./runtimeConfig";

/**
 * Issue #2006: what a person reads when Microsoft Entra ID, Entra External ID or MSAL refuses a
 * sign-in. The one mapping for every place such an error can reach the screen: the redirect
 * callback (`handleRedirectPromise` at boot, which is also where a step-up or a re-authentication
 * comes back), a sign-in that cannot start, a silent renewal's log line, the step-up dialogs and
 * the MCP consent page.
 *
 * Microsoft's own text is never shown: it names the person's directory, the resource's `api://`
 * address, a trace id and a timestamp ("AADSTS500011: The resource principal named api://... was
 * not found in the tenant named ..."). The person reads a plain sentence that says who can fix
 * it, and support gets the code (`AADSTS500011`, or MSAL's own code such as
 * `interaction_in_progress`) in small text beside the page's reference id. Only a code that has
 * the shape of a code is ever printed, never a word of the message.
 */

/** Who can fix it: the person, their organization's administrator, or Skypoint. */
export type SignInFixer = "person" | "organization" | "skypoint";

export interface SignInErrorView {
  title: string;
  message: string;
  /** The code to print in small text for support, or null when there is none to print. */
  code: string | null;
  /** The person stopped it themselves: a warning, not a failure. */
  cancelled: boolean;
  fixer: SignInFixer;
}

export const SIGN_IN_ERROR_TITLES = {
  failed: "Sign-in did not finish",
  notStarted: "Sign-in did not start",
  cancelled: "Sign-in was cancelled",
  unavailable: "Sign-in is unavailable",
} as const;

/** Every sentence, so tests and the copy check read one list. No semicolons, no dashes. */
export const SIGN_IN_ERROR_SENTENCES = {
  notApproved:
    "Your organization hasn't approved skyData for sign-in yet. Ask your IT administrator, or contact Skypoint support.",
  accountNotHere:
    "This account can't sign in here. Use your work account, or ask your IT administrator for access.",
  skypointSetup:
    "Sign-in for skyData isn't set up correctly. Contact Skypoint support with the reference id.",
  accessPolicy:
    "Your organization's access policy blocked this sign-in. Ask your IT administrator why this sign-in was blocked.",
  mfaRequired:
    "Your organization requires a second sign-in step. Sign in again and complete the step on Microsoft's page.",
  mfaSetup:
    "Your organization requires a second sign-in step that isn't set up for your account yet. Sign in again and follow the steps on Microsoft's page.",
  cancelled: "You cancelled sign-in. Choose a way to sign in when you're ready.",
  popupBlocked:
    "Your browser blocked the sign-in window. Allow pop-ups for this site, then try again.",
  inProgress:
    "A sign-in is already in progress in this browser. Finish it or close its tab, then try again.",
  storage:
    "This browser would not let skyData keep your sign-in. Check that it allows site data for skyData, then try again.",
  network: "skyData could not reach the sign-in service. Check your connection, then try again.",
  unavailable:
    "skyData could not start sign-in. Try again. If it keeps happening, contact Skypoint support with the reference id.",
  neutral:
    "Sign-in did not finish. Try again. If it keeps happening, contact Skypoint support with the reference id.",
} as const;

type SentenceKey = keyof typeof SIGN_IN_ERROR_SENTENCES;

interface Rule {
  sentence: SentenceKey;
  fixer: SignInFixer;
  cancelled?: boolean;
}

const NOT_APPROVED: Rule = { sentence: "notApproved", fixer: "organization" };
const ACCOUNT_NOT_HERE: Rule = { sentence: "accountNotHere", fixer: "organization" };
const SKYPOINT_SETUP: Rule = { sentence: "skypointSetup", fixer: "skypoint" };
const CANCELLED: Rule = { sentence: "cancelled", fixer: "person", cancelled: true };
const NETWORK: Rule = { sentence: "network", fixer: "person" };
/** A Conditional Access policy of the person's organization refused the sign-in. */
const ACCESS_POLICY: Rule = { sentence: "accessPolicy", fixer: "organization" };

/** Entra's `AADSTS` numbers. A number not listed reads the neutral sentence. */
const AADSTS_RULES: Readonly<Record<string, Rule>> = {
  // The app is not installed, or not consented to, in the person's directory.
  "500011": NOT_APPROVED,
  "65001": NOT_APPROVED,
  // Application not found in the directory: not installed there by its administrator.
  "700016": NOT_APPROVED,
  // The directory only lets an administrator approve skyData (the "Need admin approval" page).
  "90094": NOT_APPROVED,
  "90095": NOT_APPROVED,
  // The account is not in the directory this sign-in serves (50034: no such user).
  "50020": ACCOUNT_NOT_HERE,
  "90072": ACCOUNT_NOT_HERE,
  "50034": ACCOUNT_NOT_HERE,
  // The reply address is not registered on the app: Skypoint's own setup.
  "50011": SKYPOINT_SETUP,
  // A second factor is required (50076), or must be registered first (50079).
  "50076": { sentence: "mfaRequired", fixer: "person" },
  "50079": { sentence: "mfaSetup", fixer: "person" },
  // Consent was not granted. Entra sends this number for a Cancel on the consent prompt and for
  // "Return to the application without granting consent" on the admin approval page alike, and the
  // person cannot tell which fixes it, so the administrator's approval is the way forward (#2006).
  "65004": NOT_APPROVED,
  // Conditional Access: blocked by policy (53003), or the device or place is not one the policy
  // admits (53000 not compliant, 53001 not joined, 53002 an app the policy does not approve).
  "53000": ACCESS_POLICY,
  "53001": ACCESS_POLICY,
  "53002": ACCESS_POLICY,
  "53003": ACCESS_POLICY,
};

/** MSAL's own codes, when there is no `AADSTS` number. */
const MSAL_RULES: Readonly<Record<string, Rule>> = {
  user_cancelled: CANCELLED,
  popup_window_error: { sentence: "popupBlocked", fixer: "person" },
  empty_window_error: { sentence: "popupBlocked", fixer: "person" },
  interaction_in_progress: { sentence: "inProgress", fixer: "person" },
  no_network_connectivity: NETWORK,
  network_error: NETWORK,
  post_request_failed: NETWORK,
  endpoints_resolution_error: NETWORK,
  timed_out: NETWORK,
  consent_required: NOT_APPROVED,
  // A guard only (#2291): msal-browser 5.22 throws this from its storage constructors but falls
  // back to memory storage inside `getStorageImplementation`, so no call surfaces it today. If a
  // later version lets it through, it is the one MSAL case where the site-data advice is the fix.
  // The second return in a row (`useAutoSignIn.ts`) is what covers storage at run time.
  storage_not_supported: { sentence: "storage", fixer: "person" },
  // OAuth's refusal from the authorize endpoint with no `AADSTS` number the page can read. A
  // number, when there is one, decides first, and a cancel is read before this row (`ruleFor`).
  // What is left is the consent block (rehearsal, 10/26): their administrator is who can fix it.
  access_denied: NOT_APPROVED,
};

const NEUTRAL: Rule = { sentence: "neutral", fixer: "skypoint" };
/** A start-up that threw something that is not the identity provider's answer. */
const UNAVAILABLE: Rule = { sentence: "unavailable", fixer: "skypoint" };

const AADSTS_PATTERN = /AADSTS(\d{4,7})\b/;
/** The shape of an MSAL or OAuth error code: lower case words joined by underscores. */
const CODE_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

function stringField(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const field = (value as Record<string, unknown>)[key];
  return typeof field === "string" ? field : undefined;
}

function numberField(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const field = (value as Record<string, unknown>)[key];
  return typeof field === "number" && Number.isInteger(field) ? String(field) : undefined;
}

/** Whether `cause` is an MSAL error (an `AuthError` or one of its subclasses), by its shape. */
export function isMsalError(cause: unknown): boolean {
  const code = stringField(cause, "errorCode");
  return code !== undefined && stringField(cause, "errorMessage") !== undefined;
}

/** The `AADSTS` number in the error, from MSAL's `errorNo` or its message, else undefined. */
function aadstsNumber(cause: unknown): string | undefined {
  const errorNo = stringField(cause, "errorNo") ?? numberField(cause, "errorNo");
  if (errorNo && /^\d{4,7}$/.test(errorNo)) return errorNo;
  for (const text of [stringField(cause, "errorMessage"), stringField(cause, "message")]) {
    const match = text ? AADSTS_PATTERN.exec(text) : null;
    if (match) return match[1];
  }
  return undefined;
}

/**
 * The code support reads, safe to print and to log: `AADSTS<number>` when Entra gave one, else
 * MSAL's own code when it has the shape of a code, else null. Never a word of the message.
 */
export function signInErrorCode(cause: unknown): string | null {
  const number = aadstsNumber(cause);
  if (number) return `AADSTS${number}`;
  const code = stringField(cause, "errorCode");
  return code && CODE_PATTERN.test(code) ? code : null;
}

function saysCancelled(cause: unknown): boolean {
  return /cancel/i.test(stringField(cause, "errorMessage") ?? "");
}

function ruleFor(cause: unknown): Rule {
  const number = aadstsNumber(cause);
  if (number) return AADSTS_RULES[number] ?? NEUTRAL;
  const code = stringField(cause, "errorCode");
  // A message that says the person cancelled (External ID's AADB2C90091) stays the cancelled
  // sentence. `subError` is not read: MSAL fills it from `suberror` only, so nobody has seen the
  // real shape of a declined consent, and a bare `access_denied` reads not-approved either way.
  if (code === "access_denied" && saysCancelled(cause)) return CANCELLED;
  return (code && MSAL_RULES[code]) || NEUTRAL;
}

/**
 * A refusal from the identity provider did not finish. Anything else stopped the page before it
 * could offer a way in (a start-up that threw), which the page has always called unavailable.
 */
function titleOf(cause: unknown, cancelled: boolean): string {
  if (cancelled) return SIGN_IN_ERROR_TITLES.cancelled;
  return isMsalError(cause) || aadstsNumber(cause)
    ? SIGN_IN_ERROR_TITLES.failed
    : SIGN_IN_ERROR_TITLES.unavailable;
}

/**
 * What the sign-in page (and every other sign-in surface) shows for `cause`. The sign-in
 * settings' own failure keeps its sentence, which is skyData's own copy. Anything else that is
 * not an MSAL error reads the neutral sentence: its text is never shown.
 */
export function signInErrorView(cause: unknown): SignInErrorView {
  if (cause instanceof AuthConfigUnavailableError) {
    return {
      title: SIGN_IN_ERROR_TITLES.unavailable,
      message: cause.message,
      code: null,
      cancelled: false,
      fixer: "person",
    };
  }
  const rule = isMsalError(cause) || aadstsNumber(cause) ? ruleFor(cause) : UNAVAILABLE;
  const cancelled = rule.cancelled === true;
  return {
    title: titleOf(cause, cancelled),
    message: SIGN_IN_ERROR_SENTENCES[rule.sentence],
    code: signInErrorCode(cause),
    cancelled,
    fixer: rule.fixer,
  };
}

/**
 * The view as one line, for a surface whose error slot takes a string (the step-up dialogs):
 * the sentence, then the code and the reference id support looks it up by.
 */
export function signInErrorText(cause: unknown, reference: string): string {
  const view = signInErrorView(cause);
  const code = view.code ? ` Code ${view.code}.` : "";
  return `${view.message}${code} Reference id ${reference}.`;
}
